import { useCallback, useEffect } from "react";
import { matchPath, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Safely read localStorage JSON.
function readStorage(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);

    if (!raw || raw === "undefined" || raw === "null") {
      return fallback;
    }

    return JSON.parse(raw) ?? fallback;
  } catch {
    return fallback;
  }
}

// Normalize URLs.
function normalizePath(path) {
  if (!path || typeof path !== "string") return "/";

  return path.split(/[?#]/)[0].trim().replace(/\/+$/, "") || "/";
}

// Match exact paths and dynamic route patterns.
function matchesRoute(permissionPath, requestedPath) {
  if (!permissionPath || !requestedPath) return false;

  const granted = normalizePath(permissionPath);
  const requested = normalizePath(requestedPath);

  try {
    return Boolean(matchPath({ path: granted, end: true }, requested));
  } catch {
    return false;
  }
}

export default function usePrivateLink() {
  const navigate = useNavigate();

  const userData = readStorage("user_data", {});
  const client = readStorage("client", {});
  const userPermissions = readStorage("user_permissions", {});

  // New staff access system.
  const staffAccess = readStorage("staff_access", []);

  const isLoggedIn = Boolean(userData?.remember_token && client?.username);

  const isSuperAdmin = String(userData?.role_id ?? "") === "1";

  useEffect(() => {
    if (!isLoggedIn) {
      Swal.fire({
        icon: "info",
        text: "Please Login",
        confirmButtonText: "OK",
        timer: 2000,
      }).then(() => {
        navigate("/user/verify", { replace: true });
      });
    }
  }, [isLoggedIn, navigate]);

  const hasAccess = useCallback(
    (route) => {
      if (!isLoggedIn || !route) return false;

      const requestedPath = normalizePath(route);

      // Dashboard is available to every logged-in user.
      if (requestedPath === "/") return true;

      // Super Admin has access to every route.
      if (isSuperAdmin) return true;

      // Other users require explicit staff_access permission.
      const permissions = Array.isArray(staffAccess) ? staffAccess : [];

      return permissions.some((permission) => {
        const permissionPath = permission?.frontend_url;

        if (!permissionPath) return false;

        return matchesRoute(permissionPath, requestedPath);
      });
    },
    [isLoggedIn, isSuperAdmin, staffAccess],
  );

  return hasAccess;
}
